import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import isNetworkError from 'is-network-error';

import { monitoring } from '@services/monitoring';

const isJWTError = (err) =>
  err.code === 'jwt-expired' || err.response?.code === 'jwt-expired';

// A query/mutation can opt in via `meta.expectedErrorStatuses` to mark specific
// HTTP status codes as an expected, already-handled outcome (e.g. a 404 that the
// UI renders a fallback for) so it isn't reported to Datadog as an application error.
export const isExpectedError = (error, meta) => {
  const statusCode = error?.response?.status;

  return (
    statusCode === 401 ||
    statusCode === 403 ||
    isJWTError(error) ||
    isNetworkError(error) ||
    Boolean(meta?.expectedErrorStatuses?.includes(statusCode))
  );
};

const handleError = (error, reportTag, meta) => {
  if (isExpectedError(error, meta)) {
    return;
  }

  const requestUuid = error?.response?.headers?.get('x-request-id');

  monitoring.reportError(error, {
    requestId: requestUuid,
    reportedFrom: reportTag,
  });
};

// Init react query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (isNetworkError(error)) {
          return false;
        }

        if (isJWTError(error)) {
          return false;
        }

        if (
          'response' in error &&
          error?.response?.status >= 400 &&
          error?.response?.status < 500
        ) {
          return false;
        }

        return failureCount < 3;
      },
      // Don't refetch if the query has failed
      refetchOnWindowFocus(query) {
        return query.state.status !== 'error';
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleError(error, 'query', query.meta);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      handleError(error, 'mutation', mutation.meta);
    },
  }),
});

export default queryClient;
